import React from 'react';

const CapacityErrorModal = ({ isOpen, onClose, error }) => {
  if (!isOpen || !error) return null;

  const {
    roomName,
    roomNumber,
    adults,
    children,
    maxAdultsAllowed,
    maxChildrenAllowed,
    maxExtraBedsAllowed,
    baseAdults,
    extraBeds,
    totalAdultsAllowed,
    childrenAllowed,
    extraBedsNeeded,
    errorType
  } = error;

  // Calculate totals
  const totalGuestsAllowed = totalAdultsAllowed + childrenAllowed;
  const totalSelectedGuests = adults + children;

  // Build allowed description with clear extra bed info
  let allowedDescription = [];
  
  // Adults with extra bed info
  if (maxAdultsAllowed > 0) {
    if (maxExtraBedsAllowed > 0) {
      allowedDescription.push(`${maxAdultsAllowed} adult${maxAdultsAllowed > 1 ? 's' : ''} + ${maxExtraBedsAllowed} extra bed${maxExtraBedsAllowed > 1 ? 's' : ''}`);
    } else {
      allowedDescription.push(`${maxAdultsAllowed} adult${maxAdultsAllowed > 1 ? 's' : ''}`);
    }
  }
  
  // Children
  if (maxChildrenAllowed > 0) {
    allowedDescription.push(`${maxChildrenAllowed} child${maxChildrenAllowed > 1 ? 'ren' : ''}`);
  }
  
  const allowedText = allowedDescription.join(' and ');

  // Build selected description
  let selectedDescription = [];
  if (adults > 0) {
    selectedDescription.push(`${adults} adult${adults > 1 ? 's' : ''}`);
  }
  if (children > 0) {
    selectedDescription.push(`${children} child${children > 1 ? 'ren' : ''}`);
  }
  const selectedText = selectedDescription.join(' and ');

  // Calculate extra beds needed message
  const getExtraBedMessage = () => {
    if (maxExtraBedsAllowed === 0) return '';
    
    const extraAdultsNeeded = Math.max(0, adults - maxAdultsAllowed);
    const extraChildrenNeeded = Math.max(0, children - maxChildrenAllowed);
    
    if (extraAdultsNeeded > 0 && extraChildrenNeeded > 0) {
      return `Need ${extraAdultsNeeded + extraChildrenNeeded} extra bed${(extraAdultsNeeded + extraChildrenNeeded) > 1 ? 's' : ''} for ${extraAdultsNeeded} extra adult${extraAdultsNeeded > 1 ? 's' : ''} and ${extraChildrenNeeded} extra child${extraChildrenNeeded > 1 ? 'ren' : ''}`;
    } else if (extraAdultsNeeded > 0) {
      return `Need ${extraAdultsNeeded} extra bed${extraAdultsNeeded > 1 ? 's' : ''} for ${extraAdultsNeeded} extra adult${extraAdultsNeeded > 1 ? 's' : ''}`;
    } else if (extraChildrenNeeded > 0) {
      return `Need ${extraChildrenNeeded} extra bed${extraChildrenNeeded > 1 ? 's' : ''} for ${extraChildrenNeeded} extra child${extraChildrenNeeded > 1 ? 'ren' : ''}`;
    }
    return '';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Background overlay */}
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Modal panel - Centered */}
        <div className="relative inline-block align-middle bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:max-w-md sm:w-full">
          {/* Header */}
          <div className="bg-red-600 px-6 py-4">
            <div className="flex items-center justify-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-xl font-bold text-white" id="modal-title">
                  Guest Limit Exceeded
                </h3>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="bg-white px-6 py-8">
            {/* Room Info */}
            <div className="text-center mb-6">
              <p className="text-sm text-gray-500 mb-1">Room</p>
              <p className="text-xl font-bold text-gray-900">{roomName}</p>
              {roomNumber && (
                <p className="text-sm text-gray-600 mt-1">Room #{roomNumber}</p>
              )}
            </div>

            {/* Comparison */}
            <div className="space-y-4">
              {/* Allowed */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded-full bg-green-200 flex items-center justify-center">
                    <svg className="w-3 h-3 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="font-medium text-green-800">Room Capacity</span>
                </div>
                <p className="text-base font-medium text-green-800">
                  {allowedText}
                </p>
                {maxExtraBedsAllowed > 0 && (
                  <p className="text-xs text-green-600 mt-1">
                    Extra beds can be used for adults OR children
                  </p>
                )}
              </div>

              {/* Selected */}
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded-full bg-red-200 flex items-center justify-center">
                    <svg className="w-3 h-3 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </span>
                  <span className="font-medium text-red-800">You Selected</span>
                </div>
                <p className="text-base font-medium text-red-800">
                  {selectedText}
                </p>
                
              </div>

             
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-center border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center rounded-lg border border-transparent shadow-sm px-8 py-2.5 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm transition-all duration-200 min-w-[120px]"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapacityErrorModal;